import { useRef, useState, type ReactNode } from "react";
import type { VoiceStudyControlsGate } from "#voice-study-controls-gate";
import { VoiceStudyStart } from "#voice-study-start";
import { VoiceStudyWaveform } from "#voice-study-waveform";
import { cn } from "@-/com.npmjs.cn";
import { Modal, ScrollView, Text, View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { AboveKeyboard } from "@-/dev.improve.haniverse.components.above-keyboard";
import { IconButton } from "@-/dev.improve.haniverse.components.ui.button.icon";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import {
  useVoiceStudyController,
  useVoiceStudySessionDraft,
  useVoiceStudySessionLocked,
  useVoiceStudySessionMessages,
  useVoiceStudySessionPaused,
  type VoiceStudyMessage,
  type VoiceStudySessionHandle,
} from "@-/dev.improve.haniverse.lib.voice-study";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";

/**
 * One line of the voice chat. The app's prompts sit on the left, what was heard (or typed) on the
 * right; a transcript the session dropped (echo during a prompt, a busy handler) is dimmed so the
 * user still sees exactly what the recognizer picked up.
 */
function VoiceStudyChatBubble({ message }: { message: VoiceStudyMessage }) {
  const isUser = message.role === "user";
  return (
    <View
      className={cn(
        "max-w-[85%] rounded-xs px-3 py-1.5",
        isUser ? "self-end bg-primary" : "self-start bg-muted",
        message.ignored === true && "opacity-50",
      )}
      testID={`voice-study-message-${message.role}`}
    >
      <Text
        className={cn(
          "text-sm",
          isUser ? "text-primary-foreground" : "text-foreground",
          message.kind === "error" && "text-destructive",
          message.ignored === true && "italic",
        )}
      >
        {message.text}
      </Text>
    </View>
  );
}

function VoiceStudyChat({ messages }: { messages: readonly VoiceStudyMessage[] }) {
  const chatRef = useRef<ScrollView>(null);
  return (
    <ScrollView
      className="max-h-56"
      contentContainerClassName="gap-1.5 p-1"
      keyboardShouldPersistTaps="handled"
      onContentSizeChange={() => chatRef.current?.scrollToEnd({ animated: true })}
      ref={chatRef}
      testID="voice-study-chat"
    >
      {messages.map((message) => (
        <VoiceStudyChatBubble key={message.id} message={message} />
      ))}
    </ScrollView>
  );
}

function VoiceStudyControlsPill({ children }: { children: ReactNode }) {
  return (
    <AboveKeyboard className="inset-x-3 z-50 items-center" floating>
      <View
        className="w-full max-w-md gap-2 rounded-xs border border-border bg-background px-3 py-2 shadow-lg"
        testID="voice-study-controls"
      >
        {children}
      </View>
    </AboveKeyboard>
  );
}

function VoiceStudyExitButton({ onPress }: { onPress: () => void }) {
  return (
    <IconButton
      accessibilityLabel="Exit Voice"
      className="size-11"
      name="LuX"
      onPress={onPress}
      testID="voice-study-exit"
    />
  );
}

/**
 * The floating Voice pill: a chat transcript of what the app said and what it heard, the control
 * buttons, and — behind More → Input — a text box that feeds the session as if the recognizer had
 * heard the text. That box is how the voice flow is exercised where no microphone exists (Maestro,
 * the simulator).
 */
export function VoiceStudyControls({ gate }: { gate: VoiceStudyControlsGate }) {
  switch (gate.kind) {
    case "starting": {
      return <StartingVoiceStudyControls />;
    }
    case "active": {
      return <ActiveVoiceStudyControls session={gate.session} />;
    }
    case "failed": {
      return <FailedVoiceStudyControls error={gate.error} />;
    }
  }
}

function StartingVoiceStudyControls() {
  const { stop } = useVoiceStudyController();
  return (
    <VoiceStudyControlsPill>
      <View className="flex-row items-center justify-center gap-2">
        <Text className="text-sm text-muted-foreground">Starting voice study…</Text>
        <VoiceStudyExitButton onPress={stop} />
      </View>
    </VoiceStudyControlsPill>
  );
}

/** Starting failed (or the session broke outside a run): the reason, and the way out. */
function FailedVoiceStudyControls({ error }: { error: string }) {
  const { stop } = useVoiceStudyController();
  return (
    <VoiceStudyControlsPill>
      <VoiceStudyChat messages={[{ id: 0, kind: "error", role: "app", text: error }]} />
      <View className="flex-row items-center justify-center gap-2">
        <VoiceStudyExitButton onPress={stop} />
      </View>
    </VoiceStudyControlsPill>
  );
}

/** Re-renders per message; the parent (the Learn layout) does not. */
function ActiveVoiceStudyControls({ session }: { session: VoiceStudySessionHandle }) {
  const messages = useVoiceStudySessionMessages(session);
  const paused = useVoiceStudySessionPaused(session);
  const locked = useVoiceStudySessionLocked(session);
  const [muted, setMuted] = useContextSelectors(
    NavContext,
    (c) => c.isMutedOverride,
    (c) => c.setIsMutedOverride,
  );
  // Local on purpose: reopening the pill in a new session starts with the menus closed.
  const [moreOpen, setMoreOpen] = useState(false);
  const [inputVisible, setInputVisible] = useState(false);
  return (
    <>
      <VoiceStudyStart />
      <VoiceStudyControlsPill>
        {messages.length > 0 && <VoiceStudyChat messages={messages} />}
        <View className="flex-row items-center justify-center gap-2">
          <IconButton
            accessibilityLabel={muted ? "Unmute Voice" : "Mute Voice"}
            className="size-11"
            name={muted ? "MdVolumeOff" : "MdVolumeUp"}
            onPress={() => setMuted(!muted)}
            testID="voice-study-mute"
          />
          <IconButton
            accessibilityLabel={paused ? "Resume Voice" : "Pause Voice"}
            className="size-11"
            name={paused ? "LuMic" : "LuMicOff"}
            onPress={session.togglePaused}
            testID="voice-study-pause"
          />
          <IconButton
            accessibilityLabel="Repeat card"
            className="size-11"
            disabled={paused}
            name="LuRotateCcw"
            onPress={session.repeat}
            testID="voice-study-repeat"
          />
          <IconButton
            accessibilityLabel="Lock touch"
            className="size-11"
            name="MdLockOutline"
            onPress={() => session.setLocked(true)}
            testID="voice-study-lock"
          />
          <IconButton
            accessibilityLabel={moreOpen ? "Hide more voice options" : "More voice options"}
            active={moreOpen}
            className="size-11"
            name="LuMoreHorizontal"
            onPress={() => setMoreOpen((open) => !open)}
            testID="voice-study-more"
          />
          <VoiceStudyExitButton onPress={session.stop} />
        </View>
        <VoiceStudyWaveform enabled={!paused} />
        {moreOpen && (
          <View
            className="flex-row flex-wrap items-center justify-center gap-2"
            testID="voice-study-more-menu"
          >
            <IconButton
              accessibilityLabel={inputVisible ? "Hide voice input" : "Show voice input"}
              active={inputVisible}
              label="Input"
              name="LuKeyboard"
              onPress={() => setInputVisible((visible) => !visible)}
              testID="voice-study-more-input"
            />
          </View>
        )}
        {inputVisible && <VoiceStudyInput session={session} />}
      </VoiceStudyControlsPill>
      <Modal
        animationType="fade"
        onRequestClose={() => session.setLocked(false)}
        transparent
        visible={locked}
      >
        <View
          accessibilityViewIsModal
          className="flex-1 items-center justify-center bg-background/95 px-8"
          onStartShouldSetResponder={() => true}
          testID="voice-study-touch-lock"
        >
          <Text className="mb-3 text-xl font-semibold text-foreground">Touch locked</Text>
          <Text className="mb-6 text-center text-base text-muted-foreground">
            {paused
              ? "Voice is paused. Change the volume or hold the button below to unlock."
              : "Voice study continues. Say “unlock,” change the volume, or hold the button below."}
          </Text>
          <LongPressable
            accessibilityLabel="Hold to unlock touch"
            accessibilityRole="button"
            className="rounded-full border border-border bg-muted px-6 py-4"
            delayLongPress={1000}
            onLongPress={() => session.setLocked(false)}
            testID="voice-study-unlock"
          >
            <Text className="text-base font-medium text-foreground">Hold to unlock</Text>
          </LongPressable>
        </View>
      </Modal>
    </>
  );
}

/** The emulated-speech input. Its draft lives in the session, so it outlives this box. */
function VoiceStudyInput({ session }: { session: VoiceStudySessionHandle }) {
  const draft = useVoiceStudySessionDraft(session);
  const submit = () => {
    const text = draft.trim();
    if (text.length === 0) {
      return;
    }
    session.submitTranscript(text);
    session.setDraft("");
  };
  return (
    <View className="flex-row items-center gap-2">
      <Input
        accessibilityLabel="Voice input"
        autoCapitalize="none"
        autoCorrect={false}
        blurOnSubmit={false}
        className="h-11 flex-1"
        onChangeText={session.setDraft}
        onSubmitEditing={submit}
        placeholder="Type what you would say…"
        returnKeyType="send"
        testID="voice-study-input"
        value={draft}
      />
      <IconButton
        accessibilityLabel="Send voice input"
        className="size-11"
        disabled={draft.trim().length === 0}
        name="LuSend"
        onPress={submit}
        testID="voice-study-send"
      />
    </View>
  );
}
